/**
 * DateOfBirthField — Flatpickr-backed DOB picker.
 *
 * Why Flatpickr instead of <input type="date">:
 *   Safari's native date input renders today's date as a "ghost" inside empty
 *   fields and won't commit .value until every segment is user-typed. Parents
 *   edit only the year, click Continue, and validation rejects with "Date of
 *   birth is required" because Safari reports an empty .value. Apple has not
 *   fixed this (open since at least 2023). Flatpickr renders its own picker
 *   inside a plain text input, so the bug class is impossible.
 *
 * Public API matches the previous implementation so all call sites
 * (parent registration, unified athlete modal, signup, athlete profile, etc.)
 * keep working unchanged. The onChange callback receives a synthesized
 * React.ChangeEvent whose target.value is YYYY-MM-DD (database format),
 * while the visible input shows MM-DD-YYYY (US display format).
 */

"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import flatpickr from "flatpickr";
import type { Instance } from "flatpickr/dist/types/instance";
import "flatpickr/dist/flatpickr.css";
import Label from "./Label";
import { calculateAge } from "@/utils/athletes/athleteUtils";
import { formatDateOnly, parseDateLocal } from "@/utils/formatters/dateUtils";

// Default error for the adult (18+) self-registration gate. Honest about WHY
// the age matters and where to go instead — never "you're too old/young" dead ends.
export const ADULT_MIN_AGE_DEFAULT_MESSAGE =
  "Athletes under 18 need a parent or guardian to create the account. A parent or guardian can add them as an athlete and invite them to their own login afterward.";

interface DateOfBirthFieldProps {
  id: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onValidationChange?: (isValid: boolean, error?: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean | string;
  hint?: string;
  className?: string;
  disabled?: boolean;
  // Age range (in years)
  minAge?: number;
  maxAge?: number;
  // Copy shown when the entered DOB is below minAge. Defaults to the honest
  // parent-or-guardian redirect for the 18+ gate; other minAge values keep the
  // generic "Must be at least N years old." unless a message is provided.
  minAgeMessage?: string;
  // Optional context label for age error messages (e.g. "This program" or program name)
  // When provided, maxAge errors become "This program is for athletes {maxAge} and under."
  ageContextLabel?: string;
}

const DateOfBirthField: React.FC<DateOfBirthFieldProps> = ({
  id,
  name,
  value,
  onChange,
  onBlur,
  onValidationChange,
  label = "Date of Birth",
  placeholder = "MM-DD-YYYY",
  required = false,
  error = false,
  hint,
  className = "",
  disabled = false,
  minAge = 0,
  maxAge = 100,
  minAgeMessage,
  ageContextLabel,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const fpRef = useRef<Instance | null>(null);
  const [usesNativeMobileInput, setUsesNativeMobileInput] = useState(false);
  // Latest onChange in a ref so the Flatpickr instance (created once) always
  // calls the freshest callback without us re-creating the picker on every
  // parent re-render.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Compute age-bound dates and the JS-side validation error.
  const { minDate, maxDate, calculatedError } = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();

    const maxDateObj = new Date(today);
    if (minAge > 0) {
      maxDateObj.setFullYear(currentYear - minAge);
    }

    const minDateObj = new Date(today);
    minDateObj.setFullYear(currentYear - maxAge);

    let validationError: string | undefined = undefined;
    if (value) {
      const age = calculateAge(value);
      if (age === -1) {
        validationError = "Please enter a valid date of birth.";
      } else if (minAge > 0 && age < minAge) {
        validationError =
          minAgeMessage ??
          (minAge === 18
            ? ADULT_MIN_AGE_DEFAULT_MESSAGE
            : `Must be at least ${minAge} years old.`);
      } else if (maxAge < 100 && age > maxAge) {
        validationError = ageContextLabel
          ? `${ageContextLabel} is for athletes ${maxAge} and under.`
          : `Must be ${maxAge} years old or under.`;
      }
    }

    return { minDate: minDateObj, maxDate: maxDateObj, calculatedError: validationError };
  }, [minAge, maxAge, value, minAgeMessage, ageContextLabel]);

  const hasError = !!error || !!calculatedError;
  const errorText = typeof error === "string" ? error : calculatedError;

  // Initialize Flatpickr once on mount.
  useEffect(() => {
    if (!inputRef.current) return;

    const fp = flatpickr(inputRef.current, {
      // Display the date as MM-DD-YYYY directly in the visible input. We don't
      // use altInput because React re-renders keep resetting the original
      // input's `type` attribute, which makes Flatpickr's auto-hide flicker.
      // We compute the YYYY-MM-DD backing value from the Date object in onChange.
      dateFormat: "m-d-Y",
      allowInput: true, // parents can type the date directly
      // Hand Flatpickr a Date object derived from the YYYY-MM-DD value so it
      // doesn't try to parse the string with its display dateFormat ("m-d-Y").
      defaultDate: value ? parseDateLocal(value) : undefined,
      minDate,
      maxDate,
      onChange: (dates) => {
        const target = inputRef.current as HTMLInputElement;
        // Convert the picked Date object into YYYY-MM-DD for the consumer
        // (database format). Empty selection → empty string. We pass a plain
        // object as `target` (not the DOM node) because consumers only read
        // `target.value` and `target.name`; cloning the DOM node would invoke
        // getters that throw "Illegal invocation" off-element.
        const ymd = dates[0] ? formatDateOnly(dates[0]) : "";
        const syntheticEvent = {
          target: { name, value: ymd },
          currentTarget: target,
          type: "change",
          bubbles: true,
          cancelable: false,
          defaultPrevented: false,
          eventPhase: 0,
          isTrusted: false,
          nativeEvent: new Event("change"),
          preventDefault: () => {},
          isDefaultPrevented: () => false,
          stopPropagation: () => {},
          isPropagationStopped: () => false,
          persist: () => {},
          timeStamp: Date.now(),
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        onChangeRef.current(syntheticEvent);
      },
    });

    fpRef.current = Array.isArray(fp) ? fp[0] : fp;
    const instance = fpRef.current;

    if (instance?.isMobile && instance.mobileInput) {
      setUsesNativeMobileInput(true);
    }

    return () => {
      fpRef.current?.destroy();
      fpRef.current = null;
    };
    // Init once; subsequent prop changes flow through dedicated effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync controlled value into Flatpickr when the parent updates it externally.
  useEffect(() => {
    const fp = fpRef.current;
    if (!fp) return;
    const currentSelected = fp.selectedDates[0];
    const currentStr = currentSelected ? formatDateOnly(currentSelected) : "";
    if (value !== currentStr) {
      // Pass a Date object (not a YYYY-MM-DD string) so Flatpickr doesn't try
      // to parse it with the display dateFormat "m-d-Y" and silently fail.
      fp.setDate(value ? parseDateLocal(value) : "", false);
    }
  }, [value]);

  // Sync min/max bounds.
  useEffect(() => {
    const fp = fpRef.current;
    if (!fp) return;
    fp.set("minDate", minDate);
    fp.set("maxDate", maxDate);
  }, [minDate, maxDate]);

  // Requirements Summary: Flatpickr inserts a second native date input on
  // mobile; keep that as the only visible control and keep its visual state in
  // sync with the React-owned backing input.
  useEffect(() => {
    const mobileInput = fpRef.current?.mobileInput;
    if (!mobileInput) return;

    mobileInput.className = `${getInputClasses(hasError, disabled)} flatpickr-mobile`;
    mobileInput.placeholder = placeholder;
    mobileInput.required = required;
    mobileInput.disabled = disabled;
    mobileInput.setAttribute("aria-invalid", String(hasError));
    mobileInput.setAttribute("aria-label", label);
  }, [disabled, hasError, label, placeholder, required]);


  // Report validation result back to parent (used by ParentRegistrationWizard).
  const lastReportedError = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (onValidationChange && lastReportedError.current !== calculatedError) {
      lastReportedError.current = calculatedError;
      onValidationChange(!calculatedError, calculatedError);
    }
  }, [calculatedError, onValidationChange]);

  return (
    <div className={className}>
      <Label htmlFor={id} required={required}>
        {label}
      </Label>
      <input
        ref={inputRef}
        id={id}
        name={name}
        type={usesNativeMobileInput ? "hidden" : "text"}
        placeholder={placeholder}
        required={usesNativeMobileInput ? false : required}
        disabled={disabled}
        onBlur={onBlur}
        autoComplete="off"
        aria-hidden={usesNativeMobileInput ? true : undefined}
        tabIndex={usesNativeMobileInput ? -1 : undefined}
        // On mobile, Flatpickr inserts a native input beside this backing input.
        // Keep this backing input hidden so families never see two DOB fields.
        className={usesNativeMobileInput ? "hidden" : getInputClasses(hasError, disabled)}
      />
      {(errorText || hint) && (
        <p
          className={`mt-1.5 text-xs ${
            hasError ? "text-error-500" : "text-gray-500"
          }`}
        >
          {errorText || hint}
        </p>
      )}
    </div>
  );
};

// Mirror TextField's input styling so the swap is visually invisible to
// downstream call sites that previously rendered <TextField type="date" />.
function getInputClasses(hasError: boolean, disabled: boolean): string {
  const base =
    "h-11 w-full rounded-lg border appearance-none px-4 py-2.5 text-sm shadow-sm placeholder:text-gray-400 focus:outline-hidden focus:ring-3 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30";
  if (disabled) {
    return `${base} text-gray-500 border-gray-300 opacity-40 bg-gray-100 cursor-not-allowed dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700`;
  }
  if (hasError) {
    return `${base} border-error-500 focus:border-error-300 focus:ring-error-500/20 dark:text-error-400 dark:border-error-500 dark:focus:border-error-800`;
  }
  return `${base} bg-transparent text-gray-800 border-gray-300 focus:border-brand-300 focus:ring-brand-500/20 dark:border-gray-700 dark:text-white/90 dark:focus:border-brand-800`;
}

export default DateOfBirthField;
